import { useState, useContext, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment-timezone'

import { Button, Skeleton } from '@mui/material'

import { AppContext } from '/providers/app-context'
import { getClientPaymentCard, getStripeSessionResponse } from '/providers/studio-api'
import StripePaymentForm from './stripe-payment-form.jsx'

import expiredStamp from '/assets/images/expired-stamp.png'
import clockStamp from '/assets/images/clock.png'

import './credit-card.css'

const PaymentDetails = () => {

  const { apiSource, apiKey, client, refreshClient } = useContext(AppContext)
  const { stripeSessionId } = useParams()

  const [card, setCard] = useState()
  const [noCard, setNoCard] = useState(true)
  const [loading, setLoading] = useState(true)
  const [registered, setRegistered] = useState(false)

  useEffect(() => {
    getSavedCard()
  }, [stripeSessionId])

  const getSavedCard = async () => {
    const c = await getClientPaymentCard({ apiSource, apiKey })
    setLoading(false)

    if (c.hasSavedCard) {
      setNoCard(false)
      setCard(c.card)
    } else {
      setNoCard(true)
    }
  }

  const checkoutComplete = async (stripeSessionId) => {
    const status = await getStripeSessionResponse({ apiSource, apiKey, stripeSessionId })

    if (status.session && status.payment) {
      setRegistered(true)
      getSavedCard()
      refreshClient()
    }
  }

  if (loading) return (
    <div className='flex flex-col items-center justify-around relative p-20'>
      <div className='flex flex-col gap-5 p-10'>
        <Skeleton variant="rounded" width={290} height={182} />
        <Skeleton variant="rounded" width={350} height={60} />
        <Skeleton variant="rounded" width={180} height={60} />
      </div>
    </div>
  )

  return (
    <div className='flex flex-col items-center justify-around relative p-20'>
        <div className='flex flex-col gap-5 p-10'>
          <NoCard noCard={noCard} />
          <Card card={card} client={client} />
          <RegistrationDone registered={registered} />
          <Setup setRegistered={setRegistered} checkoutComplete={checkoutComplete} />
        </div>
    </div>
  )
}

export default PaymentDetails

const NoCard = ({ noCard }) => {

  if (noCard) return (
    <div className='flex flex-col items-center justify-around relative pt-20'>
      <div className="cardPlaceholder" />
      <div className='mt-5 text-2xl'>You have not yet registered a payment card</div>
    </div>
  )
}

const RegistrationDone = ({ registered }) => {
  if (registered) return (
    <div className='flex flex-col items-center justify-around relative pt-10 text-2xl text-green-500'>
      Card registration sucessful. Thank you.
    </div>
  )
}

const Card = ({ card, client }) => {

  if (card) {
    const now = moment()
    const daysleft = moment(card.expiry_date, "YYYY-MM-DD").diff(now, "days")
    const hasExpired = daysleft < 0
    const expiresSoon = daysleft < 60

    return (
      <div className='flex flex-col items-center justify-around relative pt-20'>
        <CreditCardImage cardIssuer={card.brand} last4={card.last4} cardExpiry={`${card.expiry_month}/${card.expiry_year}`} clientName={client.name} />

        {hasExpired && <>
          <div className='absolute' style={{ top: -10, left: 120 }}>
            <img src={expiredStamp} width={300} />
          </div>
          <div className='mt-5 text-2xl text-red-500'>Your payment card has expired</div>
          <div className='mt-5 text-2xl'>Please register a new card</div>
        </>}

        {expiresSoon && !hasExpired && <>
          <div className='absolute' style={{ top: -10, left: 120 }}>
            <img src={clockStamp} width={200} />
          </div>
          <div className='mt-5 text-2xl'>Your payment card expires soon</div>
          <div className='mt-5 text-2xl'>Please register a new card</div>
        </>}

        {!expiresSoon && !hasExpired && <>
          <div className='mt-5 text-2xl'>Your payment card is valid until the end of {moment(card.expiry_date, "YYYY-MM-DD").format("MMMM YYYY")}</div>
        </>}


      </div>
    )
  }
}


const Setup = ({ checkoutComplete, setRegistered }) => {
  const [open, setOpen] = useState(false)

  const openSetupForm = () => {
    setRegistered(false)
    setOpen(true)
  }

  return (
    <div className='flex flex-col items-center justify-around relative pt-10'>
      <Button onClick={openSetupForm} variant='contained'>Add a payment card</Button>
      <StripePaymentForm open={open} setOpen={setOpen} checkoutComplete={checkoutComplete} />
    </div>
  )
}


const CreditCardImage = ({ cardIssuer, last4, cardExpiry, clientName }) => {
  return (
    <div key="Cards" className="rccs" data-testid="rccs">
      <div
        data-testid="rccs__card"
        className={[
          "rccs__card",
          `rccs__card--${cardIssuer}`,
        ]
          .join(" ")
          .trim()}
      >
        <div className="rccs__card--front">
          <div className="rccs__card__background" />
          <div className="rccs__issuer" />
          <div
            className={[
              "rccs__cvc__front",
            ]
              .join(" ")
              .trim()}
          >
          </div>
          <div
            className={[
              "rccs__number",
            ]
              .join(" ")
              .trim()}
          >
            {"•••• •••• •••• " + last4}
          </div>
          <div
            className={[
              "rccs__name",
              "rccs--filled",
            ]
              .join(" ")
              .trim()}
          >
            {clientName}
          </div>
          <div
            className={[
              "rccs__expiry",
              "rccs--filled",
            ]
              .join(" ")
              .trim()}
          >
            <div className="rccs__expiry__valid"></div>
            <div className="rccs__expiry__value">{cardExpiry}</div>
          </div>
          <div className="rccs__chip" />
        </div>
      </div>
    </div>
  )
}
